import { c as _c } from "react/compiler-runtime";
import clsx from "classnames";
import { ContentSummaryTag } from "common/components/ContentSummaryTag";
import { Heading } from "common/components/Heading";
import { ImgixImage } from "common/components/ImgixImage";
import { closestCrop } from "modules/editor/utils/closestCrop";
import { ImageAspectRatio } from "modules/editor/types";
import { Image, Maybe } from "common/types/graphql";
import type { ContentSummaryVariantProps } from "common/components/ContentSummary/types";
interface BaseSummaryImageProps {
  image?: Maybe<Partial<Image>>;
  title?: string;
  imageSizes?: string;
  squareClassName?: string;
  wideClassName?: string;
}

/**
 * `BaseSummaryImage` is a functional component that renders an image with different crops based on the screen size.
 * The component uses the `closestCrop` function to determine the best crop for the image based on the provided aspect ratio.
 * If the image source is undefined, the component will return null.
 * For medium and larger screens, a widescreen crop is used, and for small screens, a square crop is used.
 */
export const BaseSummaryImage = t0 => {
  "use memo";

  const $ = _c(32);
  const {
    image,
    imageSizes,
    wideClassName: t1,
    squareClassName: t2
  } = t0;
  const wideClassName = t1 === undefined ? "" : t1;
  const squareClassName = t2 === undefined ? "" : t2;
  if (image?.src === undefined) {
    return null;
  }
  let t3;
  if ($[0] !== image.crops) {
    t3 = image.crops || [];
    $[0] = image.crops;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3) {
    t4 = closestCrop(ImageAspectRatio.Square, t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const squareCrop = t4;
  let t5;
  if ($[4] !== image.crops) {
    t5 = image.crops || [];
    $[4] = image.crops;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = closestCrop(ImageAspectRatio.Widescreen, t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const wideCrop = t6;
  let t7;
  if ($[8] !== image || $[9] !== imageSizes) {
    t7 = {
      ...image,
      src: image.src,
      sizes: imageSizes
    };
    $[8] = image;
    $[9] = imageSizes;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const imageProps = t7;
  let t8;
  if ($[11] !== wideClassName) {
    t8 = clsx("relative col-span-3 hidden aspect-video sm:block", wideClassName);
    $[11] = wideClassName;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== image.alt || $[14] !== imageProps || $[15] !== wideCrop) {
    t9 = <ImgixImage {...imageProps} crop={wideCrop} alt={image.alt} />;
    $[13] = image.alt;
    $[14] = imageProps;
    $[15] = wideCrop;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className={t8}>{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== squareClassName) {
    t11 = clsx("relative col-span-1 aspect-square sm:hidden", squareClassName);
    $[20] = squareClassName;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== image.alt || $[23] !== imageProps || $[24] !== squareCrop) {
    t12 = <ImgixImage {...imageProps} crop={squareCrop} alt={image.alt} />;
    $[22] = image.alt;
    $[23] = imageProps;
    $[24] = squareCrop;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <div className={t11}>{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <>{t10}{t13}</>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
export const BaseTitleBylineTag = t0 => {
  "use memo";

  const $ = _c(13);
  const {
    title,
    byline,
    hideByline,
    contentType,
    tagLabel,
    tagStyle
  } = t0;
  const t1 = title || "";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="group-hover:text-indigo-600 group-hover:underline"><Heading level={3} styledAsLevel={5}><span dangerouslySetInnerHTML={{
          __html: t1
        }} /></Heading></span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== byline || $[3] !== hideByline) {
    t3 = byline && !hideByline && <p className="text-3xs mt-1 line-clamp-4 text-gray-600">{byline}</p>;
    $[2] = byline;
    $[3] = hideByline;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== contentType || $[6] !== tagLabel || $[7] !== tagStyle) {
    t4 = <div className="mt-2 empty:mt-0"><ContentSummaryTag contentType={contentType} tagLabel={tagLabel} tagStyle={tagStyle} /></div>;
    $[5] = contentType;
    $[6] = tagLabel;
    $[7] = tagStyle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <section className="order-first col-span-2 self-start sm:order-last sm:col-span-3">{t2}{t3}{t4}</section>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};

/**
 * The `Base` component is a variant of the `ContentSummary` component.
 * The summary image can use two different crops due to its aspect ratio per device: square or widescreen.
 * The crop aspect ratio depends on the screen size, where smaller devices will use the square aspect ratio.
 */
export const Base = t0 => {
  "use memo";

  const $ = _c(20);
  let anchorTarget;
  let className;
  let image;
  let imageSizes;
  let props;
  let url;
  if ($[0] !== t0) {
    ({
      url,
      imageSizes,
      image,
      anchorTarget,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = anchorTarget;
    $[2] = className;
    $[3] = image;
    $[4] = imageSizes;
    $[5] = props;
    $[6] = url;
  } else {
    anchorTarget = $[1];
    className = $[2];
    image = $[3];
    imageSizes = $[4];
    props = $[5];
    url = $[6];
  }
  let t1;
  if ($[7] !== className) {
    t1 = clsx("group grid h-full w-full grid-cols-3 content-start gap-x-4 gap-y-2 outline-offset-4 2xl:gap-x-8", className);
    $[7] = className;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== props) {
    t2 = <BaseTitleBylineTag {...props} />;
    $[9] = props;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== image || $[12] !== imageSizes) {
    t3 = <BaseSummaryImage image={image} imageSizes={imageSizes} />;
    $[11] = image;
    $[12] = imageSizes;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== anchorTarget || $[15] !== t1 || $[16] !== t2 || $[17] !== t3 || $[18] !== url) {
    t4 = <article className="contents"><a href={url} target={anchorTarget} className={t1}>{t2}{t3}</a></article>;
    $[14] = anchorTarget;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = url;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};